import { createAction, createSlice, } from "@reduxjs/toolkit";

export const ScreenTypes = {
    QuizDetail: 0,
    QuestionScreen: 1,
    Results: 3,
    DiagnosticTestResult: 4,
    Complete: 5,
    Evaluating: 2,
}

const initialState = {
	loading: false,
	error: "",
	currentScreen: ScreenTypes.QuizDetail,
    quizTopic: '',
    quizType: null,
    questions: [],
    groupQuestionCount: 1,
    questionGroups: new Map(),
    currentQuestionGroup: {},
    result: [],
    gradingResult: {
        grade: null,
        passed: null,
        timeTaken: null,
        scored: null,
        total: null,
        endedAt: null,
        percentage: null,
        speechEvaluationScore: null,
        activityScore: 0,
    },
    irLevelMapping: {
        diagnosticAssessmentUrl: null,
        levelAssignment: null
    },
    selectedDiagnosticTestMapping: {},
    courseAssigned: null,
    nextCourseId: null,
    currentQuestion: 0,
    quizDetails: {
        noOfQuestions: 0,
        totalMarks: 0,
        totalTime: 0,
        selectedQuizTopic: '',
        gradingModel: {},
        isTimed: 0,
        passGrade: null,
        type: null
    },
    speechEvaluationScore: null,
    overallPercentage: null,
    currentSpeechSentance: null,
    evaluationId: "ff8081819503d341019503d88e590000",
    currentCourseDetails: {
        sectionId: null,
        chapterId: null,
        courseId: null,
        assessmentId: null
    },
    evaluationResult: {}
};

export const quizSlice = createSlice({
	name: "quiz",
	initialState,
	reducers: {
		setLoading(state, action) {
			state.loading = action.payload;
		},
        setCurrentScreen(state, action) {
            state.currentScreen = action.payload;
        },
        setResult(state, action) {
            state.result = action.payload;
        },
        setCurrentQuestionGroup(state, action) {
            state.currentQuestionGroup = action.payload
        },
        setGroupQuestionCount(state, action) {
            state.groupQuestionCount = action.payload
        },
        setQuestions(state, action) {
            let questions = action.payload,
                shuffled = [];

            let questionGroups = new Map();
            questions = questions.sort((a, b) => a.groupDetails?.order - b.groupDetails?.order);

            questions.forEach(e => {
                if(e.groupDetails && !questionGroups.has(e.groupDetails.id)) {
                    let noOfQuestions = questions.filter(question => question.groupDetails?.id === e.groupDetails?.id).length;
                    questionGroups.set(e.groupDetails.id, {...e.groupDetails, noOfQuestions});
                }
            });
            
            for (const groupId of questionGroups.keys()) {
                if (groupId) {
                    let _questions = questions.filter(e => e.groupDetails?.id == groupId);
                    _questions.length && (shuffled = shuffled.concat(_.shuffle(_questions)));
                }
            }

            if (!shuffled.length) {
                shuffled = _.shuffle(questions);
            }

            state.questions = shuffled.map(e => {
                let {answerDetails, type} = e;

                if (answerDetails && answerDetails.options) {
                    if (answerDetails.options.values) {
                        e['answerDetails']['options']['values'] = _.shuffle(answerDetails.options.values);
                    }

                    if (type == 'MTF') {
                        answerDetails['options']['MTFLHS'] = _.shuffle(answerDetails.options.MTFLHS);
                        answerDetails['options']['MTFRHS'] = _.shuffle(answerDetails.options.MTFRHS);
                    }
                }

                return e;
            });

            state.questionGroups = questionGroups;
            state.currentQuestionGroup = Array.from(questionGroups.values())[0];

        },
        setQuizDetails(state, action) {
            state.quizDetails = action.payload;
        },
        setQuizTopic(state, action) {
            state.quizTopic = action.payload;
        },
        setQuizType(state, action) {
            state.quizType = action.payload;
        },
        setGradingResult(state, action) {
            state.gradingResult = action.payload
        },
        setIRLevelMapping(state, action) {
            state.irLevelMapping = action.payload;
        },
        setCourseAssigned(state, action) {
            state.courseAssigned = action.payload;
        },
        setNextCourseId(state, action) {
            state.nextCourseId = action.payload;
        },
        setSpeechEvaluationScore(state, action) {
            state.speechEvaluationScore = action.payload;
        },
        setOverallPercentage(state, action) {
            state.overallPercentage = action.payload;
        },
        setCurrentSpeechSentance(state, action) {
            state.currentSpeechSentance = action.payload;
        },
        setCurrentQuestion(state, action) {
            state.currentQuestion = action.payload;
        },
        setSelectedDiagnosticTestMapping(state, action) {
            state.selectedDiagnosticTestMapping = action.payload;
        },
        setEvaluationId(state, action) {
            state.evaluationId = action.payload;
        },
        setCurrentCourseDetails(state, action) {
            state.currentCourseDetails = action.payload;
        },
        updateCurrentCourseDetails(state, action) {
            state.currentCourseDetails = {...state.currentCourseDetails, ...action.payload};
        },
        setEvaluationResult(state, action) {
            state.evaluationResult = action.payload;
        },
	},
});

export const quizActions = {
    setCurrentScreen: quizSlice.actions.setCurrentScreen,
    setResult: quizSlice.actions.setResult,
    setQuestionGroup: quizSlice.actions.setCurrentQuestionGroup,
    setGroupQuestionCount: quizSlice.actions.setGroupQuestionCount,
    setQuestions: quizSlice.actions.setQuestions,
    setQuizDetails: quizSlice.actions.setQuizDetails,
    setQuizTopic: quizSlice.actions.setQuizTopic,
    setType: quizSlice.actions.setQuizType,
    setGradingResult: quizSlice.actions.setGradingResult,
    setIRLevelMapping: quizSlice.actions.setIRLevelMapping,
    setCourseAssigned: quizSlice.actions.setCourseAssigned,
    setNextCourseId: quizSlice.actions.setNextCourseId,
    setStudyState: quizSlice.actions.setStudyState,
    setSpeechEvaluationScore: quizSlice.actions.setSpeechEvaluationScore,
    setOverallPercentage: quizSlice.actions.setOverallPercentage,
    setCurrentSpeechSentance: quizSlice.actions.setCurrentSpeechSentance,
    setCurrentQuestion: quizSlice.actions.setCurrentQuestion,
    setSelectedDiagnosticTestMapping: quizSlice.actions.setSelectedDiagnosticTestMapping,
    setEvaluationId: quizSlice.actions.setEvaluationId,
    setCurrentCourseDetails: quizSlice.actions.setCurrentCourseDetails,
    updateCurrentCourseDetails: quizSlice.actions.updateCurrentCourseDetails,
    setEvaluationResult: quizSlice.actions.setEvaluationResult,
};

export const isQuizLoading = state => state.quiz.loading;
export const getQuizDetail = state => state.quiz.quizDetails;
export const getCurrent = state => state.quiz.currentScreen;
export const getQuestions = state => state.quiz.questions;
export const getResult = state => state.quiz.result;
export const getCurrentQuestionGroup = state => state.quiz.currentQuestionGroup;
export const getGroupQuestionCount = state => state.quiz.groupQuestionCount;
export const getQuestionGroup = state => state.quiz.currentQuestionGroup;
export const getQuestionGroups = state => state.quiz.questionGroups;
export const getQuizTopic = state => state.quiz.quizTopic;
export const getType = state => state.quiz.quizType;
export const getGradingResult = state => state.quiz.gradingResult;
export const getIRLevelMapping = state => state.quiz.irLevelMapping;
export const courseAssigned = state => state.quiz.courseAssigned;
export const nextCourseId = state => state.quiz.nextCourseId;
export const studyState = state => state.quiz.studyState;
export const speechEvaluationScore = state => state.quiz.speechEvaluationScore;
export const getOverallPercentage = state => state.quiz.overallPercentage;
export const currentSpeechSentance = state => state.quiz.currentSpeechSentance;
export const currentQuestion = state => state.quiz.currentQuestion;
export const selectedDiagnosticTestMapping = state => state.quiz.selectedDiagnosticTestMapping;
export const evaluationId = state => state.quiz.evaluationId;
export const currentCourseDetails = state => state.quiz.currentCourseDetails;
export const evaluationResult = state => state.quiz.evaluationResult;

export default quizSlice.reducer;
